
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Sidebar from 'primevue/sidebar';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import SelectButton from 'primevue/selectbutton';
import { cloneDeep } from 'lodash';
import { SortOrderEnum } from '@/types/enums';
import Filter from '@/components/table/Filter.vue';
import type { ColumnFilterMatchModeOptions } from 'primevue/column';
import { SpmTableControlDescriptor, SpmTableSort } from '@/types/table-types';

interface ApplyPayload {
  filters: Record<string, any>;
  sort: SpmTableSort | null;
}

export default defineComponent({
  name: 'SpmTableControlsPanel',

  components: {
    Sidebar,
    Button,
    Dropdown,
    SelectButton,
    Filter,
  },

  props: {
    visible: {
      type: Boolean,
      required: true,
    },
    controls: {
      type: Array as PropType<SpmTableControlDescriptor[]>,
      required: true,
    },
    filters: {
      type: Object as PropType<Record<string, any>>,
      required: true,
    },
    sort: {
      type: Object as PropType<SpmTableSort | null>,
      required: false,
      default: null,
    },
  },

  emits: ['update:visible', 'apply', 'reset'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const internalVisible = ref(props.visible);
    const localFilters = reactive<Record<string, any>>({});
    const localSortField = ref<string | null>(null);
    const localSortOrder = ref<SortOrderEnum>(SortOrderEnum.ASC);

    const filterControls = computed(() => props.controls.filter((control) => control.supportsFiltering));

    const textMatchModeOptions = computed<ColumnFilterMatchModeOptions[]>(() => ([
      { label: t('filters.contains'), value: 'contains' },
      { label: t('filters.startsWith'), value: 'startsWith' },
      { label: t('filters.endsWith'), value: 'endsWith' },
      { label: t('filters.equals'), value: 'equals' },
      { label: t('filters.notEquals'), value: 'notEquals' },
      { label: t('filters.notContains'), value: 'notContains' },
      { label: t('filters.empty'), value: 'empty' },
      { label: t('filters.notEmpty'), value: 'notEmpty' },
    ]));

    const resolveFilterType = (control: SpmTableControlDescriptor): string | null => {
      const rawType = control.column.filterSettings?.type ?? control.column.type ?? null;

      if (!rawType || typeof rawType !== 'string') {
        return null;
      }

      return rawType.toLowerCase();
    };

    const isTextualFilter = (control: SpmTableControlDescriptor): boolean => {
      const type = resolveFilterType(control);

      if (!type) {
        return true;
      }

      return ![
        'dropdown',
        'select',
        'multiselect',
        'multi-select',
        'language',
        'country',
        'date',
        'datetime',
        'calendar',
      ].includes(type);
    };

    const matchModeOptions = computed<Record<string, ColumnFilterMatchModeOptions[]>>(() => {
      const map: Record<string, ColumnFilterMatchModeOptions[]> = {};

      filterControls.value.forEach((control) => {
        const explicitMatchModes = control.column.matchModes ?? [];
        const options = explicitMatchModes.length
          ? explicitMatchModes
          : (isTextualFilter(control) ? textMatchModeOptions.value : []);

        if (options.length) {
          map[control.filterKey] = options;
        }
      });

      return map;
    });

    const sortingOptions = computed(() => {
      const seen = new Set<string>();
      return props.controls
        .filter((control) => control.supportsSorting && control.sortField)
        .reduce<{ label: string; value: string; displayField?: string }[]>((acc, control) => {
          if (control.sortField && !seen.has(control.sortField)) {
            acc.push({ label: control.label, value: control.sortField });
            seen.add(control.sortField);
          }
          return acc;
        }, []);
    });

    const sortOrderOptions = computed(() => ([
      { label: t('tableControls.sorting.asc'), value: SortOrderEnum.ASC },
      { label: t('tableControls.sorting.desc'), value: SortOrderEnum.DESC },
    ]));

    const noop = () => {};

    const cloneFilterMeta = (source?: Record<string, any>) => {
      if (!source) {
        return {};
      }
      return cloneDeep(source);
    };

    const ensureLocalFilters = () => {
      const cloned = cloneFilterMeta(props.filters);

      Object.keys(localFilters).forEach((key) => {
        delete localFilters[key];
      });

      filterControls.value.forEach((control) => {
        const filterKey = control.filterKey;
        const filterMeta = cloned[filterKey];
        const availableMatchModes = matchModeOptions.value[filterKey] ?? [];
        const defaultMatchMode = control.column.filterSettings?.defaultMatchMode
          ?? control.column.matchModes?.[0]?.value
          ?? availableMatchModes[0]?.value
          ?? 'contains';

        if (filterMeta) {
          const constraints = Array.isArray(filterMeta.constraints) && filterMeta.constraints.length
            ? filterMeta.constraints
            : [{ value: '', matchMode: defaultMatchMode }];

          if (!constraints[0].matchMode || !availableMatchModes.some((mode) => mode.value === constraints[0].matchMode)) {
            constraints[0].matchMode = defaultMatchMode;
          }

          localFilters[filterKey] = {
            ...filterMeta,
            operator: filterMeta.operator ?? null,
            constraints,
          };
        } else {
          localFilters[filterKey] = {
            operator: null,
            constraints: [{
              value: '',
              matchMode: defaultMatchMode,
            }],
          };
        }
      });
    };

    const initializeState = () => {
      ensureLocalFilters();
      if (props.sort && props.sort.field) {
        localSortField.value = props.sort.field;
        localSortOrder.value = props.sort.type as SortOrderEnum;
      } else {
        localSortField.value = null;
        localSortOrder.value = SortOrderEnum.ASC;
      }
    };

    watch(() => props.visible, (value) => {
      internalVisible.value = value;
      if (value) {
        initializeState();
      }
    });

    watch(() => props.filters, () => {
      if (internalVisible.value) {
        ensureLocalFilters();
      }
    }, { deep: true });

    watch([filterControls, matchModeOptions], () => {
      if (internalVisible.value) {
        ensureLocalFilters();
      }
    });

    watch(internalVisible, (value) => {
      emit('update:visible', value);
    });

    const updateFilterValue = (key: string, value: unknown) => {
      if (!localFilters[key]) {
        return;
      }
      localFilters[key].constraints[0].value = value;
    };

    const onApply = () => {
      const payload: ApplyPayload = {
        filters: cloneFilterMeta(localFilters),
        sort: localSortField.value
          ? { field: localSortField.value, type: localSortOrder.value }
          : null,
      };

      emit('apply', payload);
      internalVisible.value = false;
    };

    const onReset = () => {
      emit('reset');
    };

    const closePanel = () => {
      internalVisible.value = false;
    };

    onMounted(() => {
      if (props.visible) {
        initializeState();
      }
    });

    return {
      t,
      internalVisible,
      localFilters,
      localSortField,
      localSortOrder,
      sortingOptions,
      filterControls,
      matchModeOptions,
      sortOrderOptions,
      updateFilterValue,
      onApply,
      onReset,
      closePanel,
      noop,
    };
  },
});
